import React, { useCallback, useEffect, useState } from "react";
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { throttle, debounce } from 'throttle-debounce';
import {
  Box,
  Typography,
  Slider,
  IconButton,
  Input,
  InputLabel,
  InputAdornment,
  useTheme,
  Theme
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { useTranslation } from "react-i18next";
import { UnitConverter } from "../viewer/utilities";
import { isEqual } from 'lodash';
import { Clamp } from "../redux/settings-slice";
import { Units } from "../types/project";

const updateValueInterval = 50;
const saveDispatchInterval = 1000;

const converter = new UnitConverter();

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    listItem: {
      width: '100%',
      marginBottom: theme.spacing(0.5)
    },
    inputPropParent: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      zoom: "0.85",
      marginRight: `calc(36px + ${theme.spacing(1.5)})`,
    },
    inputPropTitle: {
      height: "fit-content"
    }
  }),
);

interface GenericSliderProps {
  value: number | Clamp;
  min: number;
  max: number;
  step: number;
  onChange: (value) => void;
  onFinish?: (value) => void;
  labelFunction?: (value) => string;
  magnitude?: number;
  units?: Units;
  endAdornment?: string;
}

interface InputSliderProps extends GenericSliderProps {
  type: "single" | "range";
}

const InputSlider = (props: InputSliderProps) => {
  const {value, type, onChange, onFinish, labelFunction,
    magnitude=1.0, units=Units.Meters, endAdornment=""} = props;

  const classes = useStyles();
  const theme = useTheme();

  const min = converter.convert(props.min * magnitude, Units.Meters, units);
  const max = converter.convert(props.max * magnitude, Units.Meters, units);
  const step = props.step * magnitude;
  const isRangeType = type === "range";
  const inputProps = {min, max, step};

  const defaultLabel = value => {
    return `${+Number(value).toFixed(2)}${endAdornment}`;
  };

  const labelFormat = labelFunction ? labelFunction : defaultLabel;

  const [openState, setOpen] = useState(false);
  const [sliderValue, setSliderValue] = useState(null);

  const roundToStep = (value) => {
    return +Number((Math.round(value/step) * step).toFixed(2));
  };

  const toInput = (value) => {
    if (isRangeType) {
      const min = converter.convert(value.min * magnitude, Units.Meters, units);
      const max = converter.convert(value.max * magnitude, Units.Meters, units);

      return [roundToStep(min), roundToStep(max)];
    }

    const out = converter.convert(value * magnitude, Units.Meters, units);
    return roundToStep(out);
  };

  const fromInput = (value) => {
    if (isRangeType) {
      return {
        min: converter.convert(value[0], units, Units.Meters) / magnitude,
        max: converter.convert(value[1], units, Units.Meters) / magnitude
      };
    }

    return converter.convert(value, units, Units.Meters) / magnitude;
  };

  // Throttle update function
  const changeCallback = useCallback(
    throttle(updateValueInterval, (value) => {
      onChange(value);
    }), []);

  // Debounce save function
  const finishCallback = useCallback(
    debounce(saveDispatchInterval, (value) => {
      if (!onFinish) return;
      onFinish(value);
    }), []);

  useEffect(() => {
    let scaledValue = toInput(value);

    /** NOTE: react will never return equal for arrays, so lodash is used */
    if (isEqual(sliderValue, scaledValue)) return;
    setSliderValue(scaledValue);
  }, [value, units]);

  useEffect(() => {
    if (sliderValue === null) return;

    // Trigger update and change callbacks
    const saveValue = fromInput(sliderValue);
    changeCallback(saveValue);
    finishCallback(saveValue);
  }, [sliderValue]);

  const handleClick = () => {
    setOpen(!openState);
  };

  return (
    <React.Fragment>
      <div style={{
        display: "flex",
        alignItems: "flex-end",
        paddingTop: theme.spacing(1.0)
      }}>
        <div style={{
          width: "100%"
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}>
            <Typography variant="body2">{labelFormat(min)}</Typography>
            <Typography variant="body2">{labelFormat(max)}</Typography>
          </Box>

          <Slider
            min={min}
            max={max}
            step={step}
            value={sliderValue}
            color={"secondary"}
            valueLabelDisplay="auto"
            valueLabelFormat={labelFormat}
            onChange={(event, value) => {
              setSliderValue(value);
            }}
            aria-labelledby="input-slider"
          />
        </div>

        <IconButton
          size="small"
          style={{
            marginLeft: theme.spacing(1.5),
            height: "fit-content"
          }}
          onClick={(event) => {
            event.stopPropagation();
            handleClick();
          }}
        >
          {openState ? <KeyboardArrowUp  fontSize="small"/> : <KeyboardArrowDown fontSize="small"/>}
        </IconButton>
      </div>

      {openState && (<div className={classes.listItem}>
        {isRangeType && (<RangeInputProp
          values={sliderValue}
          setValues={setSliderValue}
          inputProps={inputProps}
          adornment={endAdornment}
        />)}

        {!isRangeType && (<SingleInputProp
          value={sliderValue}
          setValue={setSliderValue}
          inputProps={inputProps}
          adornment={endAdornment}
        />)}
      </div>)}
    </React.Fragment>
  );
};

interface InputProp {
  min: number;
  max: number;
  step: number;
}

interface SingleInputPropProps {
  value: number;
  setValue: React.Dispatch<number>;
  inputProps: InputProp;
  adornment: string;
}

const SingleInputProp = (props: SingleInputPropProps) => {
  const {value, setValue, inputProps, adornment} = props;

  const classes = useStyles();
  const {t} = useTranslation();

  const [inputValue, setInputValue] = useState(0);
  const [inputError, setInputError] = useState(false);

  useEffect(() => {
    setInputValue(+Number(value).toFixed(2));
    setInputError(false);
  }, [value]);

  const hasError = (value) => {
    return (value === "")
     || isNaN(value)
     || (parseFloat(value) < inputProps.min)
     || (parseFloat(value) > inputProps.max);
  };

  return (
    <div className={classes.inputPropParent}>
      <InputLabel className={classes.inputPropTitle}>
        {t("user-settings.slider-value")}:
      </InputLabel>

      <InputField
        value={inputValue}
        inputProps={inputProps}
        error={inputError}
        adornment={adornment}
        onChange={(value) => {
          // Check for general error
          let error = hasError(value);
          setInputValue(value);
          setInputError(error);
          if (error) return;

          // Update slider value
          value = parseFloat(value);
          setValue(value);
        }}
      />
    </div>
  );
};

interface RangeInputPropProps {
  values: number[];
  setValues: React.Dispatch<number[]>;
  inputProps: InputProp;
  adornment: string;
}

const RangeInputProp = (props: RangeInputPropProps) => {
  const {values, setValues, inputProps, adornment} = props;

  const classes = useStyles();
  const {t} = useTranslation();

  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(0);

  const [minError, setMinError] = useState(false);
  const [maxError, setMaxError] = useState(false);

  useEffect(() => {
    setMinValue(+Number(values[0]).toFixed(2));
    setMaxValue(+Number(values[1]).toFixed(2));

    setMinError(false);
    setMaxError(false);
  }, [values]);

  const hasError = (value) => {
    return (value === "")
     || isNaN(value)
     || (parseFloat(value) < inputProps.min)
     || (parseFloat(value) > inputProps.max);
  };

  return (
    <React.Fragment>
      <div className={classes.inputPropParent}>
        <InputLabel className={classes.inputPropTitle}>
          {t("user-settings.slider-minimum-value")}:
        </InputLabel>

        <InputField
          value={minValue}
          inputProps={inputProps}
          adornment={adornment}
          error={minError}
          onChange={(value) => {
            // Check for general error
            let error = hasError(value);
            setMinValue(value);
            setMinError(error);
            if (error) return;

            value = parseFloat(value);

            // Make sure min is less than max
            if (value > maxValue) {
              setMinError(true);
              return;
            }

            // Update slider values
            setValues([value, maxValue]);
          }}
        />
      </div>

      <div className={classes.inputPropParent}>
        <InputLabel className={classes.inputPropTitle}>
          {t("user-settings.slider-maximum-value")}:
        </InputLabel>

        <InputField
          value={maxValue}
          inputProps={inputProps}
          adornment={adornment}
          error={maxError}
          onChange={(value) => {
            // Check for general error
            let error = hasError(value);
            setMaxValue(value);
            setMaxError(error);
            if (error) return;

            value = parseFloat(value);

            // Make sure max is more than min
            if (value < minValue) {
              setMaxError(true);
              return;
            }

            // Update slider values
            setValues([minValue, value]);
          }}
        />

      </div>
    </React.Fragment>
  );
};

interface InputFieldProps {
  value: number;
  inputProps: InputProp;
  onChange: (value) => void;
  adornment: string;
  error: boolean;
}

const InputField = (props: InputFieldProps) => {
  const {value, inputProps, onChange, adornment, error} = props;

  return (
    <Input
      fullWidth
      error={error}
      style={{
        flex: 1
      }}
      inputProps={{
        ...inputProps,
        type: "number"
      }}
      value={value}
      endAdornment={<InputAdornment position="end">{adornment}</InputAdornment>}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
};

export const SingleSlider = (props: GenericSliderProps) => {
  return <InputSlider type="single" {...props}/>;
};

export const RangeSlider = (props: GenericSliderProps) => {
  return <InputSlider type="range" {...props}/>;
};
